import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { API_URL } from '../../lib/api';
import { ADMIN_KINDS, supportApi, toastApiError } from '../../lib/support';

/**
 * Write to a customer first: starts a conversation in their Help and emails
 * it to them. Used by Support ("New message") and Users (a row's Message).
 *
 * `to` fixes the recipient ({ id, name, email }). Without it the admin
 * searches for one. `onSent` gets the new conversation; the caller says so.
 */
export function NewMessageDialog({ open, onOpenChange, onSent, to = null }) {
  const [users, setUsers] = useState([]);
  const [find, setFind] = useState('');
  const [userId, setUserId] = useState(null);
  const [kind, setKind] = useState('question');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Only the search needs the user list
  useEffect(() => {
    if (!open || to || users.length) return;
    axios
      .get(`${API_URL}/admin/users`, { headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } })
      .then((r) => setUsers(r.data.filter((u) => u.auth_type !== 'demo')))
      .catch(() => toast.error('Could not load users'));
  }, [open, to, users.length]);

  const matches = useMemo(() => {
    const term = find.trim().toLowerCase();
    if (!term) return [];
    return users.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(term)).slice(0, 6);
  }, [find, users]);
  const chosen = to || users.find((u) => u.id === userId);

  const reset = () => { setFind(''); setUserId(null); setKind('question'); setSubject(''); setMessage(''); };
  const close = () => { reset(); onOpenChange(false); };

  const submit = async () => {
    setSending(true);
    try {
      const thread = await supportApi.adminStart({ user_id: chosen.id, kind, subject, message });
      close();
      onSent(thread);
    } catch (e) {
      toastApiError(e, 'Could not send');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New message</DialogTitle>
          <DialogDescription>Starts a conversation in their Help, and emails it to them.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="support-to">To</Label>
            {chosen ? (
              <div className="flex items-center justify-between gap-3 rounded-pill bg-mist px-4 h-11 min-w-0" data-testid="support-to-chosen">
                <span className="truncate">
                  {chosen.name || chosen.email} <span className="text-muted-foreground">{chosen.email}</span>
                </span>
                {!to && (
                  <Button variant="link" size="sm" className="px-0" onClick={() => setUserId(null)}>Change</Button>
                )}
              </div>
            ) : (
              <>
                <Input id="support-to" value={find} onChange={(e) => setFind(e.target.value)} placeholder="Search by name or email" data-testid="support-to" />
                {matches.length > 0 && (
                  <ul className="rounded-cards bg-surface divide-y divide-line">
                    {matches.map((u) => (
                      <li key={u.id}>
                        <button type="button" onClick={() => setUserId(u.id)} className="w-full text-left px-4 py-2 hover:bg-mist">
                          {u.name || u.email} <span className="text-sm text-muted-foreground">{u.email}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
          <div className="grid sm:grid-cols-[160px_1fr] gap-4">
            <div className="space-y-2">
              <Label>About</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="support-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ADMIN_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="support-subject">Subject</Label>
              <Input id="support-subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={120} data-testid="support-subject" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="support-message">Message</Label>
            <Textarea id="support-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={6} data-testid="support-message" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <Button onClick={submit} disabled={sending || !chosen || !subject.trim() || !message.trim()} data-testid="support-send">
            {sending && <Loader2 className="animate-spin" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
